import { h, Prop, State, Component, Listen, Host } from '@stencil/core';
import { SelectOption } from '../../../types/types';
import { HostElement } from '@stencil/core/internal';
import { format, parse } from 'date-fns';

@Component({
  tag: 'rentware-time-picker',
  styleUrl: 'style.sass',
  shadow: false,
})
export class rentwareTimePicker {
  @Prop({ mutable: true }) value: string;
  @Prop() options: string[]; // default
  @Prop() optionsWithLabel: SelectOption[];
  @Prop() disabled?: boolean;
  @Prop() label?: string;
  @Prop() hint?: string;
  @Prop() autoClose: boolean;
  @Prop() isLoading: boolean;
  @Prop() isPlaceholderRed: boolean = false;
  @Prop() onTimeChange: (hour: string) => void;

  @State() isOpen: boolean = false;
  @State() isDisabled: boolean = false;

  inputElement: HostElement;

  @Listen('clickOutsideCalendarTime')
  clickOutsideCompletedHandler() {
    this.collapse();
  }

  togglesOpen = () => {
    if (!this.selectHasMultipleOptions) return;
    if (this.disabled) return this.collapse();
    this.isOpen = !this.isOpen;
  };

  collapse = () => {
    this.isOpen = false;
  };

  handleChange = (hour: string) => {
    this.onTimeChange(hour);
    this.collapse();
  };

  get timeOptions(): SelectOption[] {
    if (this.options?.length > 0) {
      return this.options?.map(option => {
        const dateWithTime = new Date(parse(option, 'HH:mm', new Date()));
        const localizedTime = format(dateWithTime, 'p');
        return { value: option, label: localizedTime };
      });
    }

    return this.optionsWithLabel;
  }

  get selectedLabel() {
    return this.timeOptions?.find(option => option.value === this.value)?.label;
  }

  get selectedValue() {
    return !!this.selectedLabel ? this.selectedLabel : this.value;
  }

  get selectHasMultipleOptions(): boolean {
    if (this.disabled) return false;
    if (this.options?.length === 0) return false;
    if (this.options?.length === 1) return !this.value;
    if (this.options?.length > 1) return true;
    if (!!this.optionsWithLabel) return true;
  }

  get openUp(): boolean {
    const expandedHeightWithGap = 250; // same as css
    const position = this.inputElement.getBoundingClientRect();

    if (position.bottom + expandedHeightWithGap > window.innerHeight) {
      return true;
    }

    return false;
  }

  render() {
    return (
      <Host class="OuterContainer" ref={el => (this.inputElement = el)}>
        <rentware-spinner style={this.isLoading ? {} : { display: 'none' }} withOverlay={true} />

        <button class="SelectContainer" disabled={!this.selectHasMultipleOptions} onClick={this.togglesOpen}>
          {!!this.label && <label class="Label">{this.label}</label>}

          <div class="LeftInner">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9.16667" />
              <path d="M12.9167 12.0833L10 10V4" stroke="#46435B" />
              <path d="M10 2V3" />
              <path d="M10 17V18" />
              <path d="M2.5 9.5V10.5" />
              <path d="M17.5 9.5V10.5" />
            </svg>

            <div class="TimeContainer">
              {!!this.selectedValue ? <span data-text={this.hint}>{this.selectedValue}</span> : <span class={{ Red: this.isPlaceholderRed && !this.disabled }}>{this.hint}</span>}
            </div>
          </div>

          {this.selectHasMultipleOptions && (
            <svg class="SelectChevronSVG" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          )}
        </button>

        {this.isOpen && (
          <div class={{ ExpandedContainerOuter: true, ExpandUp: this.openUp }}>
            <div class="ExpandedContainer">
              <rentware-expanded-picker autoClose={this.autoClose} options={this.timeOptions} onSelectTime={this.handleChange} />
            </div>
          </div>
        )}
      </Host>
    );
  }
}
